import type { CertificationPreview, Profession } from "@brookbush/types";

const PERSONAL_TRAINING_SLUG = "personal-training";
const CPT_STUDY_PLAN_SLUG = "cpt-certification-study-plan";

export interface CptStudyPlanPreset {
	primaryProfessionId: string;
	selectedCertificationId: string;
	isPersonalTraining: boolean;
}

/**
 * Builds the onboarding selections for the welcome modal's CPT study plan
 * shortcut. A saved profession is kept; otherwise Personal Training is used.
 *
 * @param {object} options
 * @param {Profession[] | undefined} options.professions - Loaded professions
 * @param {CertificationPreview[] | undefined} options.certifications - Loaded certifications
 * @param {string | null | undefined} options.savedPrimaryProfessionId - The account's saved primary profession
 * @returns {CptStudyPlanPreset | null} The preset, or null when its data is not available yet
 */
export function resolveCptStudyPlanPreset(options: {
	professions: Profession[] | undefined;
	certifications: CertificationPreview[] | undefined;
	savedPrimaryProfessionId: string | null | undefined;
}): CptStudyPlanPreset | null {
	const studyPlan = options.certifications?.find(
		(certification) => certification.slug === CPT_STUDY_PLAN_SLUG,
	);
	if (!studyPlan) return null;

	const personalTrainingId = options.professions?.find(
		(profession) => profession.slug === PERSONAL_TRAINING_SLUG,
	)?._id;
	const primaryProfessionId =
		options.savedPrimaryProfessionId || personalTrainingId;
	if (!primaryProfessionId) return null;

	return {
		primaryProfessionId,
		selectedCertificationId: studyPlan._id,
		isPersonalTraining: primaryProfessionId === personalTrainingId,
	};
}

/**
 * Whether the wizard still holds the preset's selections.
 *
 * @param {Pick<CptStudyPlanPreset, "primaryProfessionId" | "selectedCertificationId">} values - Current wizard selections
 * @param {CptStudyPlanPreset | null} preset - The applied preset
 * @returns {boolean} True when neither selection has been changed
 */
export function isCptStudyPlanPresetIntact(
	values: {
		primaryProfessionId?: string | null;
		selectedCertificationId?: string | null;
	},
	preset: CptStudyPlanPreset | null,
): boolean {
	return (
		!!preset &&
		values.primaryProfessionId === preset.primaryProfessionId &&
		values.selectedCertificationId === preset.selectedCertificationId
	);
}

/**
 * The note shown on the wizard's last step after the shortcut fills it in.
 *
 * @param {Pick<CptStudyPlanPreset, "isPersonalTraining">} preset - The applied preset
 * @returns {string} The note copy
 */
export function getCptStudyPlanPresetNote(
	preset: Pick<CptStudyPlanPreset, "isPersonalTraining">,
): string {
	return preset.isPersonalTraining
		? "Personal Trainer and NCCA-accredited CPT Exam Study Plan have been selected. You can change these settings at any time."
		: "NCCA-accredited CPT Exam Study Plan has been selected. You can change these settings at any time.";
}
